/*
 * 戒导航 · 戒打卡 · 个人主页 tab（/u/:username/checkin）
 * =============================================================================
 * 设计依据：doc/戒打卡功能设计方案.md §22.6（方案 A，2026-09-30 落地）。
 *
 * ★ 为什么需要这个页面 ★
 *   此前个人主页侧栏的「戒打卡」入口指向独立页 `/checkin`（自带 `.container`
 *   与整页骨架），点击后**脱离个人主页布局**，体验上与其他菜单项（回复 / 讨论 /
 *   设置 / 安全）不一致。本页面把打卡内容搬进个人主页布局内，作为一个 tab 显示，
 *   与核心的 UserSecurityPage（/u/:username/security）保持同一范式。
 *
 * ★ 复用清单（不重复造轮子，§9.4）★
 *   - `UserPage`（核心）：自带 hero（头像 / 用户名 / 简介）+ 侧栏 navItems 的整页布局，
 *     并在 `view()` 中于 `this.user` 就绪后调用子类 `content()`；
 *   - `CheckinContent`（本扩展，见 ./CheckinPage.tsx）：承载打卡全部业务逻辑与渲染，
 *     独立页与个人主页 tab 共用同一份实现，避免逻辑分叉；
 *   - `extractText`（核心）：把翻译结果（Vnode | string）规范化为纯字符串，供
 *     `app.setTitle()` 使用。
 *
 * ★ 路由注册 ★
 *   在 ./extend.ts 用 `new Extend.Routes().add('user.checkin',
 *   '/u/:username/checkin', UserCheckinPage, UserPageResolver)` 注册；
 *   `UserPageResolver`（核心）负责在 SPA 内路由到该页时按 username 预取用户数据，
 *   与核心 user.security 路由完全一致。
 *
 * ★ 后端路由 ★
 *   核心的通用路由 `/u/{username}[/{filter:[^/]*}]`（vendor/flarum/core/src/Forum/
 *   routes.php）已覆盖 `/u/{username}/checkin`（filter='checkin'），因此**无需**
 *   在 extend.php 新增 PHP 路由（与 core 的 user.security 做法一致）。
 *
 * ★ 可见范围（用户裁决 2026-09-30「仅本人可见」）★
 *   与侧栏入口「仅本人主页显示」口径一致：非本人（含游客）直接重定向回首页，
 *   避免出现「看得到别人打卡」的越权观感。后端三个 Scoper 仍以 actor 身份过滤，
 *   **无需放宽**。
 */
import app from 'flarum/forum/app';
import UserPage from 'flarum/forum/components/UserPage';
import extractText from 'flarum/common/utils/extractText';
import { CheckinContent } from './CheckinPage';
export default class UserCheckinPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);

    // 读取路由中的 username（UserPage 布局依赖 this.user；routeName = 'user.checkin'）
    const routeUsername = String(m.route.param('username') ?? '');
    const actor = app.session.user;

    // 隐私守卫：仅本人可查看自己的打卡页；他人 / 游客 → 回首页
    //（与 index.ts 侧栏入口「仅本人主页显示」保持同一口径）
    if (!actor || routeUsername !== actor.slug()) {
      m.route.set('/');
      return;
    }

    // 浏览器标签标题（与独立页共用同一翻译键，见 resources/locale/*.yml）
    app.setTitle(extractText(app.translator.trans('jiedh-checkin.forum.page_title')));

    // 加载该用户数据：UserPage 会在 user 就绪后调用 content()
    this.loadUser(routeUsername);
  }

  /**
   * 页面主体（由 UserPage 在 hero + 侧栏之间渲染）。
   *
   * 外层包 `.CheckinPage` 是为了命中 less/forum.less 的嵌套选择器
   * （`.CheckinPage .CheckinHeader` 等）；**不加 `.container`** ——
   * UserPage 的内容区本身已有容器，再加会导致双重内边距、内容过窄。
   */
  content() {
    return m("div", {
      className: "UserCheckinPage CheckinPage"
    }, m(CheckinContent, null));
  }
}
flarum.reg.add('jiedh-checkin', 'forum/components/UserCheckinPage', UserCheckinPage);